import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Server, Play, Square, RotateCw, Trash2, Code2, X, HardDrive, Camera, Maximize2, RefreshCw, TerminalSquare, Clock, CornerDownLeft, Tag, Plus, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { JsonDrawer } from "@/components/azure/AllResources";

const PORTS = [{ n: 3389, l: "RDP (3389)" }, { n: 22, l: "SSH (22)" }, { n: 80, l: "HTTP (80)" }, { n: 443, l: "HTTPS (443)" }];
const stateColor = (s) => s === "Running" ? "text-emerald-600" : s?.includes("deallocated") ? "text-muted-foreground" : "text-amber-600";

export function VirtualMachines() {
  const { state, run, refresh } = useAzure();
  const rgs = state?.resource_groups || [];
  const resources = state?.resources || [];
  const catalog = state?.catalog || { vm_images: [], vm_sizes: [] };
  const vms = resources.filter((r) => r.type === "Microsoft.Compute/virtualMachines");
  const vnets = resources.filter((r) => r.type === "Microsoft.Network/virtualNetworks");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("basics");
  const [sel, setSel] = useState(null);
  const [json, setJson] = useState(null);
  const [busy, setBusy] = useState(false);
  const blank = { name: "", rg: "", size: "Standard_B1s", image: "ubuntu2404", admin_username: "azureuser",
    vnet_name: "", subnet_name: "", public_ip: true, nsg_name: "", inbound_ports: [22], os_disk_sku: "Premium_LRS", add_data_disk: false };
  const [f, setF] = useState(blank);
  const [term, setTerm] = useState(null);
  const [winInfo, setWinInfo] = useState(null);
  const [tagRows, setTagRows] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  useEffect(() => {
    const t = sel?.tags || {};
    const rows = Object.entries(t).map(([key, value]) => ({ key, value }));
    setTagRows(rows.length ? rows : [{ key: "", value: "" }]);
  }, [sel]);
  const addTagRow = () => setTagRows((r) => [...r, { key: "", value: "" }]);
  const removeTagRow = (i) => setTagRows((r) => r.filter((_, idx) => idx !== i));
  const updateTagRow = (i, field, val) => setTagRows((r) => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  const saveTags = async () => {
    const tags = {};
    tagRows.forEach(({ key, value }) => { const k = (key || "").trim(); if (k) tags[k] = value; });
    setSavingTags(true);
    try {
      await api.post(`/azure/vms/${sel.name}/tags`, { tags });
      const fr = await api.get("/azure/state");
      setSel(fr.data.resources.find((x) => x.id === sel.id));
      await refresh();
      toast.success("Tags applied");
    } catch (e) { toast.error(azErr(e)); } finally { setSavingTags(false); }
  };

  const subnets = vnets.find((v) => v.name === f.vnet_name)?.properties.subnets || [];
  const togglePort = (n) => setF((p) => ({ ...p, inbound_ports: p.inbound_ports.includes(n) ? p.inbound_ports.filter((x) => x !== n) : [...p.inbound_ports, n] }));

  const create = async () => {
    setBusy(true);
    try {
      const body = {
        name: f.name, rg: f.rg, size: f.size, image: f.image, admin_username: f.admin_username,
        vnet_name: f.vnet_name, subnet_name: f.subnet_name, public_ip: f.public_ip,
        nsg_name: f.nsg_name || `${f.name}-nsg`, inbound_ports: f.inbound_ports,
        data_disks: f.add_data_disk ? [{ size_gb: 128, sku: "StandardSSD_LRS" }] : [],
      };
      const { data } = await api.post("/azure/vms", body);
      await refresh();
      setOpen(false);
      toast.success(`Deployment succeeded — ${data.resource.name} (${data.deployment.operations.length} resources created)`);
    } catch (e) { toast.error(azErr(e)); } finally { setBusy(false); }
  };

  const action = (vm, act) => {
    if (act === "delete" && !window.confirm(`Delete VM ${vm.name} and its disks/NIC?`)) return;
    run(() => api.post("/azure/vms/action", { vm_id: vm.id, action: act }), { success: `${act} completed` }).then(async (r) => {
      if (r.ok) { if (act === "delete") setSel(null); else { const fr = await api.get("/azure/state"); setSel(fr.data.resources.find((x) => x.id === vm.id)); } }
    });
  };
  const resize = (vm, size) => run(() => api.post("/azure/vms/resize", { vm_id: vm.id, size }), { success: "Resized" }).then(async () => { const fr = await api.get("/azure/state"); setSel(fr.data.resources.find((x) => x.id === vm.id)); });
  const snapshot = (vm) => {
    const osDisk = resources.find((r) => r.id === vm.properties.os_disk_id);
    if (!osDisk) return;
    run(() => api.post("/azure/snapshots", { name: `${vm.name}-snap-${Date.now() % 10000}`, resource_group: vm.resource_group, disk_id: osDisk.id }), { success: "Snapshot created" });
  };

  return (
    <div data-testid="az-vms">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Button size="sm" onClick={() => { setF({ ...blank, rg: rgs[0]?.name || "", vnet_name: vnets[0]?.name || "", subnet_name: vnets[0]?.properties.subnets[0]?.name || "" }); setTab("basics"); setOpen(true); }} data-testid="az-new-vm-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Server className="h-4 w-4" /> Create</Button>
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>
      <div className="border border-border rounded-md overflow-hidden">
        <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead>Size</TableHead><TableHead>Public IP</TableHead><TableHead>Resource group</TableHead></TableRow></TableHeader>
          <TableBody>
            {vms.map((v) => (
              <TableRow key={v.id} className="cursor-pointer" data-testid={`az-vm-row-${v.name}`} onClick={() => setSel(v)}>
                <TableCell className="font-medium text-[#0078d4]">{v.name}</TableCell>
                <TableCell className={`text-xs font-medium ${stateColor(v.properties.power_state)}`}>{v.properties.power_state}</TableCell>
                <TableCell>{v.properties.size}</TableCell>
                <TableCell className="font-mono text-xs">{v.properties.public_ip || "—"}</TableCell>
                <TableCell>{v.resource_group}</TableCell>
              </TableRow>
            ))}
            {vms.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No virtual machines. Click Create to deploy one (create a VNet first).</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Create wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Create a virtual machine</DialogTitle></DialogHeader>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-4">
              <TabsTrigger value="basics" data-testid="az-vm-tab-basics">Basics</TabsTrigger>
              <TabsTrigger value="disks">Disks</TabsTrigger>
              <TabsTrigger value="networking">Networking</TabsTrigger>
              <TabsTrigger value="review" data-testid="az-vm-tab-review">Review + create</TabsTrigger>
            </TabsList>
            <TabsContent value="basics" className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Resource group *</Label><Select value={f.rg} onValueChange={(v) => setF({ ...f, rg: v })}><SelectTrigger data-testid="az-vm-rg"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{rgs.map((r) => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>VM name *</Label><Input data-testid="az-vm-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="WEBVM01" /></div>
                <div className="space-y-1.5 col-span-2"><Label>Image *</Label><Select value={f.image} onValueChange={(v) => setF({ ...f, image: v })}><SelectTrigger data-testid="az-vm-image"><SelectValue /></SelectTrigger><SelectContent>{catalog.vm_images.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5 col-span-2"><Label>Size *</Label><Select value={f.size} onValueChange={(v) => setF({ ...f, size: v })}><SelectTrigger data-testid="az-vm-size"><SelectValue /></SelectTrigger><SelectContent>{catalog.vm_sizes.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} — {s.vcpus} vCPU, {s.ram_gb} GiB (~${s.cost_hr}/hr)</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5 col-span-2"><Label>Administrator username</Label><Input data-testid="az-vm-admin" value={f.admin_username} onChange={(e) => setF({ ...f, admin_username: e.target.value })} /></div>
              </div>
              <p className="text-xs text-muted-foreground">Costs are synthetic training figures, not real Azure prices.</p>
            </TabsContent>
            <TabsContent value="disks" className="space-y-3 pt-2">
              <div className="space-y-1.5"><Label>OS disk type</Label><Select value={f.os_disk_sku} onValueChange={(v) => setF({ ...f, os_disk_sku: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Standard_LRS">Standard HDD</SelectItem><SelectItem value="StandardSSD_LRS">Standard SSD</SelectItem><SelectItem value="Premium_LRS">Premium SSD</SelectItem></SelectContent></Select></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="az-vm-datadisk" checked={f.add_data_disk} onChange={(e) => setF({ ...f, add_data_disk: e.target.checked })} /> Attach a 128 GB data disk</label>
            </TabsContent>
            <TabsContent value="networking" className="space-y-3 pt-2">
              <div className="space-y-1.5"><Label>Virtual network *</Label><Select value={f.vnet_name} onValueChange={(v) => setF({ ...f, vnet_name: v, subnet_name: vnets.find((x) => x.name === v)?.properties.subnets[0]?.name || "" })}><SelectTrigger data-testid="az-vm-vnet"><SelectValue placeholder={vnets.length ? "Select" : "Create a VNet first"} /></SelectTrigger><SelectContent>{vnets.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Subnet *</Label><Select value={f.subnet_name} onValueChange={(v) => setF({ ...f, subnet_name: v })}><SelectTrigger data-testid="az-vm-subnet"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{subnets.map((s) => <SelectItem key={s.name} value={s.name}>{s.name} ({s.prefix})</SelectItem>)}</SelectContent></Select></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="az-vm-pip" checked={f.public_ip} onChange={(e) => setF({ ...f, public_ip: e.target.checked })} /> Create a public IP</label>
              <div><Label className="text-sm">Inbound ports</Label><div className="flex flex-wrap gap-3 mt-1">{PORTS.map((p) => <label key={p.n} className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={f.inbound_ports.includes(p.n)} onChange={() => togglePort(p.n)} /> {p.l}</label>)}</div>
                {f.public_ip && (f.inbound_ports.includes(3389) || f.inbound_ports.includes(22)) && <p className="text-xs text-amber-600 mt-1">Warning: exposing RDP/SSH to the Internet is a security risk. Consider Bastion.</p>}
              </div>
            </TabsContent>
            <TabsContent value="review" className="space-y-2 pt-2 text-sm">
              <div className="rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-2 text-xs">Validation passed</div>
              {[["Resource group", f.rg], ["VM name", f.name], ["Image", catalog.vm_images.find((i) => i.id === f.image)?.name], ["Size", f.size], ["Virtual network", f.vnet_name], ["Subnet", f.subnet_name], ["Public IP", f.public_ip ? "Yes" : "No"], ["Inbound ports", f.inbound_ports.join(", ") || "None"]].map(([k, v]) => <div key={k} className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium">{String(v || "—")}</span></div>)}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            {tab !== "review" ? <Button onClick={() => setTab(tab === "basics" ? "disks" : tab === "disks" ? "networking" : "review")} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">Next</Button>
              : <Button data-testid="az-vm-create-submit" onClick={create} disabled={busy || !f.name || !f.rg || !f.vnet_name || !f.subnet_name} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">{busy ? "Deploying…" : "Create"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {sel && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-vm-detail">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div><div className="font-display font-bold text-lg">{sel.name}</div><div className={`text-xs font-medium ${stateColor(sel.properties.power_state)}`}>{sel.properties.power_state}</div></div>
            <Button size="icon" variant="ghost" onClick={() => setSel(null)}><X className="h-4 w-4" /></Button>
          </div>
          <VmExpiry vm={sel} />
          <div className="px-5 py-3 border-b border-border flex flex-wrap gap-2">
            <Button size="sm" className="gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" data-testid="az-vm-connect" onClick={() => (sel.properties.os_type === "Windows" ? setWinInfo(sel) : setTerm(sel))}><TerminalSquare className="h-3.5 w-3.5" /> Connect</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="az-vm-start" onClick={() => action(sel, "start")}><Play className="h-3.5 w-3.5" /> Start</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="az-vm-restart" onClick={() => action(sel, "restart")}><RotateCw className="h-3.5 w-3.5" /> Restart</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="az-vm-stop" onClick={() => action(sel, "deallocate")}><Square className="h-3.5 w-3.5" /> Stop</Button>
            <Button size="sm" variant="outline" className="gap-1.5" data-testid="az-vm-snapshot" onClick={() => snapshot(sel)}><Camera className="h-3.5 w-3.5" /> Snapshot</Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setJson(sel)}><Code2 className="h-3.5 w-3.5" /> JSON</Button>
            <Button size="sm" variant="outline" className="gap-1.5 text-red-600" data-testid="az-vm-delete" onClick={() => action(sel, "delete")}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          </div>
          <div className="p-5 space-y-2 text-sm overflow-y-auto">
            {[["Size", sel.properties.size], ["vCPUs", sel.properties.vcpus], ["RAM", `${sel.properties.ram_gb} GiB`], ["OS", sel.properties.image], ["Private IP", sel.properties.private_ip], ["Public IP", sel.properties.public_ip || "—"], ["Subnet", sel.properties.subnet], ["Computer name", sel.properties.computer_name]].map(([k, v]) => <div key={k} className="flex justify-between border-b border-border/50 py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium">{String(v)}</span></div>)}
            <div className="pt-2"><Label className="text-xs">Resize</Label><Select onValueChange={(v) => resize(sel, v)}><SelectTrigger data-testid="az-vm-resize" className="mt-1"><SelectValue placeholder="Change size…" /></SelectTrigger><SelectContent>{catalog.vm_sizes.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="pt-4 mt-2 border-t border-border" data-testid="az-vm-tags-editor">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Tags</span>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={addTagRow} data-testid="az-vm-tag-add"><Plus className="h-3.5 w-3.5" /> Add tag</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">Tag names and values are case-sensitive (e.g. key <span className="font-mono">CostCenter</span>).</p>
              <div className="space-y-2">
                {tagRows.map((row, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`az-vm-tag-row-${i}`}>
                    <Input placeholder="Name" value={row.key} onChange={(e) => updateTagRow(i, "key", e.target.value)} className="h-8 text-xs" data-testid={`az-vm-tag-key-${i}`} />
                    <Input placeholder="Value" value={row.value} onChange={(e) => updateTagRow(i, "value", e.target.value)} className="h-8 text-xs" data-testid={`az-vm-tag-value-${i}`} />
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => removeTagRow(i)} data-testid={`az-vm-tag-remove-${i}`}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <Button size="sm" className="mt-3 gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={saveTags} data-testid="az-vm-tag-save" disabled={savingTags}>
                <Save className="h-4 w-4" /> {savingTags ? "Saving…" : "Apply tags"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <JsonDrawer resource={json} onClose={() => setJson(null)} />
      {term && <VmTerminal vm={term} onClose={() => setTerm(null)} />}
      <Dialog open={!!winInfo} onOpenChange={(o) => !o && setWinInfo(null)}>
        <DialogContent aria-describedby={undefined} data-testid="az-vm-rdp-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Server className="h-5 w-5 text-[#0078d4]" /> Connect to {winInfo?.name}</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <div className="rounded-md bg-[#0078d4]/10 text-[#0078d4] px-3 py-2 text-xs">
              This is a Windows Server. Windows machines are administered through a Remote Desktop (GUI) session, not the Linux browser SSH terminal.
            </div>
            <div className="space-y-1.5">
              {[["Computer", `${winInfo?.properties.private_ip} (${winInfo?.name})`], ["Username", winInfo?.properties.admin_username || "azureuser"], ["Password", "Password123"], ["Connection", "Remote Desktop (RDP · port 3389)"]].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border/50 py-1"><span className="text-muted-foreground">{k}</span><span className="font-medium font-mono text-xs">{String(v)}</span></div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Open the HMG Windows System Admin console to manage this server with Server Manager, PowerShell, AD/DNS/DHCP tools and more.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setWinInfo(null)}>Close</Button>
            <Button className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2" data-testid="az-vm-open-win-console" onClick={() => window.location.assign("/windows-admin")}>
              <Maximize2 className="h-4 w-4" /> Open Windows console
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VmExpiry({ vm }) {
  const expires = vm.properties.expires_at;
  const [now, setNow] = useState(Date.now());
  const fired = useRef({});
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expires) {
    if (vm.properties.deletion_state === "persistent")
      return (
        <div className="px-5 py-2.5 border-b border-border flex items-center gap-2 text-xs bg-[#0078d4]/10 text-[#0078d4]" data-testid="az-vm-persistent">
          <Server className="h-4 w-4" />
          <span>Enterprise baseline server — <strong>persistent</strong> (not auto-deleted). {vm.properties.os_type === "Windows" ? "Manage via Windows System Admin console." : "Connect via browser SSH."}</span>
        </div>
      );
    return null;
  }
  const ms = new Date(expires).getTime() - now;
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  const mins = totalSec / 60;
  [30, 10, 5, 1].forEach((thr) => {
    if (mins <= thr && !fired.current[thr] && totalSec > 0) {
      fired.current[thr] = true;
      toast.warning(`Your training VM "${vm.name}" will be permanently deleted in ${thr} minute${thr > 1 ? "s" : ""}. Save any lab results now.`);
    }
  });
  const deleteTime = new Date(expires).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const danger = totalSec <= 600;
  return (
    <div className={`px-5 py-2.5 border-b border-border flex items-center gap-2 text-xs ${danger ? "bg-red-500/10 text-red-600" : "bg-amber-500/10 text-amber-700 dark:text-amber-400"}`} data-testid="az-vm-expiry">
      <Clock className="h-4 w-4" />
      <span>Training VM expires in <strong className="font-mono tabular-nums">{hh}:{mm}:{ss}</strong></span>
      <span className="ml-auto opacity-80">Auto-deletion: {deleteTime}</span>
    </div>
  );
}

function VmTerminal({ vm, onClose }) {
  const ip = vm.properties.private_ip || vm.properties.public_ip || vm.name;
  const adminUser = vm.properties.admin_username || "azureuser";
  // Simulated SSH login gate — accepts the VM's admin user (or azureuser) with Password123.
  const isValidLogin = (u, p) => (u === adminUser || u === "azureuser") && p === "Password123";

  const [phase, setPhase] = useState("login-user"); // login-user | login-pass | shell
  const [pendingUser, setPendingUser] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [lines, setLines] = useState([
    { t: "sys", v: `Connecting to ${vm.name} (${ip}) via HMG Azure Training browser terminal…` },
    { t: "sys", v: `The authenticity of host '${vm.name} (${ip})' can't be established.` },
    { t: "out", v: `login as: ` },
  ]);
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [cwd, setCwd] = useState("/");
  const [hist, setHist] = useState([]);
  const [hi, setHi] = useState(-1);
  const bodyRef = useRef(null);
  const user = phase === "shell" ? (pendingUser || adminUser) : adminUser;

  useEffect(() => { bodyRef.current?.scrollTo(0, bodyRef.current.scrollHeight); }, [lines]);

  const startShell = (u) => {
    setPendingUser(u);
    setPhase("shell");
    const last = new Date().toUTCString();
    setLines((l) => [...l,
      { t: "sys", v: `\nWelcome to ${vm.properties.image || "Ubuntu 24.04 LTS"} (GNU/Linux)` },
      { t: "sys", v: `Last login: ${last} from 20.51.0.10` },
      { t: "sys", v: `Type commands and press Enter. This is a real isolated shell.` },
    ]);
  };

  const submitLogin = (value) => {
    if (phase === "login-user") {
      const u = value.trim();
      setLines((l) => [...l, { t: "in", v: `login as: ${u}` }, { t: "out", v: `${u}@${ip}'s password: ` }]);
      setPendingUser(u);
      setPhase("login-pass");
      setCmd("");
      return;
    }
    // login-pass
    const ok = isValidLogin(pendingUser, value);
    setLines((l) => [...l, { t: "in", v: `${pendingUser}@${ip}'s password: ` }]);
    setCmd("");
    if (ok) {
      startShell(pendingUser);
      return;
    }
    const next = attempts + 1;
    setAttempts(next);
    if (next >= 3) {
      setLines((l) => [...l, { t: "err", v: `Permission denied (publickey,password).` }, { t: "err", v: `${pendingUser}@${ip}: Connection closed.` }]);
      setPhase("closed");
      return;
    }
    setLines((l) => [...l, { t: "err", v: `Permission denied, please try again.` }, { t: "out", v: `login as: ` }]);
    setPhase("login-user");
    setPendingUser("");
  };

  const send = async () => {
    const c = cmd.trim();
    setLines((l) => [...l, { t: "in", v: `${user}@${vm.properties.computer_name}:${cwd}$ ${cmd}` }]);
    setCmd("");
    if (!c) return;
    setHist((h) => [c, ...h]); setHi(-1);
    setBusy(true);
    try {
      const { data } = await api.post(`/azure/vms/${vm.name}/exec`, { command: c, cwd });
      if (data.output) setLines((l) => [...l, { t: data.exit_code === 0 ? "out" : "err", v: data.output.replace(/\n$/, "") }]);
      if (data.cwd) setCwd(data.cwd);
    } catch (e) {
      setLines((l) => [...l, { t: "err", v: azErr(e) }]);
    } finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (phase === "login-user" || phase === "login-pass") {
      if (e.key === "Enter") { e.preventDefault(); submitLogin(cmd); }
      return;
    }
    if (e.key === "Enter") { e.preventDefault(); if (!busy) send(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); const n = Math.min(hi + 1, hist.length - 1); if (n >= 0) { setHi(n); setCmd(hist[n] || ""); } }
    else if (e.key === "ArrowDown") { e.preventDefault(); const n = Math.max(hi - 1, -1); setHi(n); setCmd(n === -1 ? "" : hist[n] || ""); }
  };

  const inLogin = phase === "login-user" || phase === "login-pass";
  const promptLabel = phase === "login-user" ? "login as:" : phase === "login-pass" ? `${pendingUser}@${ip}'s password:` : `${user}@${vm.properties.computer_name}:${cwd}$`;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="az-vm-terminal">
      <div className="w-full max-w-3xl h-[70vh] bg-[#0b0b0c] rounded-lg shadow-2xl flex flex-col overflow-hidden border border-[#333]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 h-10 bg-[#1b1a19] text-white/90 text-sm shrink-0">
          <TerminalSquare className="h-4 w-4 text-[#50c8ff]" />
          <span>{adminUser}@{vm.name} — SSH (browser)</span>
          <span className="ml-auto text-[10px] uppercase tracking-wide bg-[#8a6d00] text-[#ffe89e] px-1.5 py-0.5 rounded">Training VM</span>
          <button onClick={onClose} className="ml-2 hover:text-white text-white/60" data-testid="az-vm-terminal-close"><X className="h-4 w-4" /></button>
        </div>
        <div ref={bodyRef} className="flex-1 overflow-y-auto p-3 font-mono text-[13px] leading-relaxed">
          {lines.map((l, i) => (
            <pre key={i} className={`whitespace-pre-wrap break-words ${l.t === "in" ? "text-white" : l.t === "err" ? "text-red-400" : l.t === "sys" ? "text-[#50c8ff]" : "text-emerald-200/90"}`}>{l.v}</pre>
          ))}
          {inLogin && (
            <div className="text-[11px] text-white/40 mt-2">Hint: sign in as <span className="text-white/70">azureuser</span> / <span className="text-white/70">Password123</span></div>
          )}
        </div>
        {phase === "closed" ? (
          <div className="flex items-center justify-between px-3 py-2 border-t border-[#333] bg-[#0b0b0c] text-xs text-white/60">
            <span>Session closed — too many failed attempts.</span>
            <button onClick={() => { setPhase("login-user"); setAttempts(0); setPendingUser(""); setLines((l) => [...l, { t: "out", v: "login as: " }]); }} className="text-[#50c8ff] hover:underline" data-testid="az-vm-terminal-retry">Reconnect</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 border-t border-[#333] bg-[#0b0b0c]">
            <span className="font-mono text-[13px] text-emerald-300 shrink-0">{promptLabel}</span>
            <input autoFocus value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={onKey} disabled={busy}
              type={phase === "login-pass" ? "password" : "text"}
              data-testid="az-vm-terminal-input" spellCheck={false} autoComplete="off"
              className="flex-1 bg-transparent outline-none font-mono text-[13px] text-white" />
            <button onClick={() => (inLogin ? submitLogin(cmd) : send())} disabled={busy} className="text-white/60 hover:text-white"><CornerDownLeft className="h-4 w-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

export function Disks() {
  const { state, run, refresh } = useAzure();
  const resources = state?.resources || [];
  const rgs = state?.resource_groups || [];
  const disks = resources.filter((r) => r.type === "Microsoft.Compute/disks");
  const vms = resources.filter((r) => r.type === "Microsoft.Compute/virtualMachines");
  const snaps = resources.filter((r) => r.type === "Microsoft.Compute/snapshots");
  const SKUS = ["Standard_LRS", "StandardSSD_LRS", "Premium_LRS"];
  const del = (d) => run(() => api.post("/azure/resources/delete", { id: d.id }), { success: "Deleted" });

  const [createOpen, setCreateOpen] = useState(false);
  const [cf, setCf] = useState({ name: "", resource_group: "", size_gb: 128, sku: "StandardSSD_LRS" });
  const [dlg, setDlg] = useState(null); // {mode:'attach'|'tier', disk}
  const [pick, setPick] = useState("");

  const createDisk = async () => {
    const r = await run(() => api.post("/azure/disks", { ...cf, location: (rgs.find((g) => g.name === cf.resource_group) || {}).location }), { success: "Disk created" });
    if (r.ok) { setCreateOpen(false); setCf({ name: "", resource_group: "", size_gb: 128, sku: "StandardSSD_LRS" }); refresh(); }
  };
  const detach = async (d) => { const r = await run(() => api.post("/azure/disks/detach", { disk_id: d.id }), { success: "Disk detached" }); if (r.ok) refresh(); };
  const snapshotDisk = async (d) => {
    const r = await run(() => api.post("/azure/snapshots", { name: `${d.name}-snap-${Date.now() % 10000}`, resource_group: d.resource_group, disk_id: d.id }), { success: "Snapshot created" });
    if (r.ok) refresh();
  };
  const doDlg = async () => {
    let r;
    if (dlg.mode === "attach") r = await run(() => api.post("/azure/disks/attach", { disk_id: dlg.disk.id, vm_id: pick }), { success: "Disk attached" });
    else r = await run(() => api.post("/azure/disks/change-tier", { disk_id: dlg.disk.id, sku: pick }), { success: "Disk tier changed" });
    if (r.ok) { setDlg(null); setPick(""); refresh(); }
  };

  return (
    <div data-testid="az-disks">
      <Tabs defaultValue="disks">
        <TabsList><TabsTrigger value="disks">Disks</TabsTrigger><TabsTrigger value="snapshots">Snapshots</TabsTrigger></TabsList>
        <TabsContent value="disks" className="pt-3">
          <div className="flex mb-2"><Button size="sm" className="ml-auto gap-1.5 bg-[#0078d4] hover:bg-[#106ebe] text-white" onClick={() => setCreateOpen(true)} data-testid="az-disk-create-btn"><HardDrive className="h-4 w-4" /> Create disk</Button></div>
          <div className="border border-border rounded-md overflow-hidden">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Size</TableHead><TableHead>SKU</TableHead><TableHead>State</TableHead><TableHead>Attached VM</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{disks.map((d) => {
                const attached = d.properties.state === "Attached";
                const vm = vms.find((v) => v.id === d.properties.vm_id);
                const isOs = d.properties.disk_type === "OS";
                return (
                  <TableRow key={d.id} data-testid={`az-disk-row-${d.name}`}>
                    <TableCell className="font-medium">{d.name}{isOs && <span className="ml-1 text-[10px] text-muted-foreground">(OS)</span>}</TableCell>
                    <TableCell>{d.properties.size_gb} GB</TableCell>
                    <TableCell>{d.properties.sku}</TableCell>
                    <TableCell className="text-xs">{d.properties.state}{attached && d.properties.lun != null ? ` · LUN ${d.properties.lun}` : ""}</TableCell>
                    <TableCell className="text-xs">{vm ? vm.name : "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!isOs && (attached
                        ? <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => detach(d)} data-testid={`az-disk-detach-${d.name}`}>Detach</Button>
                        : <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => { setDlg({ mode: "attach", disk: d }); setPick(""); }} data-testid={`az-disk-attach-${d.name}`}>Attach</Button>)}
                      <Button size="sm" variant="outline" className="h-7 text-xs mr-1" onClick={() => { setDlg({ mode: "tier", disk: d }); setPick(d.properties.sku); }} data-testid={`az-disk-tier-${d.name}`}>Change tier</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs mr-1 gap-1" onClick={() => snapshotDisk(d)} data-testid={`az-disk-snapshot-${d.name}`}><Camera className="h-3.5 w-3.5" /> Snapshot</Button>
                      {!attached && !isOs && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(d)}><Trash2 className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
                {disks.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No disks.</TableCell></TableRow>}
              </TableBody></Table>
          </div>
        </TabsContent>
        <TabsContent value="snapshots" className="pt-3">
          <div className="border border-border rounded-md overflow-hidden">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Source disk</TableHead><TableHead>Size</TableHead></TableRow></TableHeader>
              <TableBody>{snaps.map((s) => (<TableRow key={s.id} data-testid={`az-snap-row-${s.name}`}><TableCell className="font-medium">{s.name}</TableCell><TableCell>{s.properties.source_disk}</TableCell><TableCell>{s.properties.size_gb} GB</TableCell></TableRow>))}
                {snaps.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No snapshots.</TableCell></TableRow>}
              </TableBody></Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="az-disk-create-dialog">
          <DialogHeader><DialogTitle>Create a managed disk</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Disk name</Label><Input value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} data-testid="az-disk-name" /></div>
            <div><Label>Resource group</Label>
              <Select value={cf.resource_group} onValueChange={(v) => setCf({ ...cf, resource_group: v })}>
                <SelectTrigger data-testid="az-disk-rg"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{rgs.map((g) => <SelectItem key={g.name} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Size (GB)</Label><Input type="number" value={cf.size_gb} onChange={(e) => setCf({ ...cf, size_gb: e.target.value })} data-testid="az-disk-size" /></div>
              <div><Label>SKU</Label>
                <Select value={cf.sku} onValueChange={(v) => setCf({ ...cf, sku: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SKUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#0078d4] text-white" disabled={!cf.name || !cf.resource_group} onClick={createDisk} data-testid="az-disk-create-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-sm" data-testid="az-disk-action-dialog">
          <DialogHeader><DialogTitle>{dlg?.mode === "attach" ? `Attach ${dlg?.disk?.name} to a VM` : `Change tier of ${dlg?.disk?.name}`}</DialogTitle></DialogHeader>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="az-disk-dlg-select"><SelectValue placeholder={dlg?.mode === "attach" ? "Select a VM" : "Select SKU"} /></SelectTrigger>
            <SelectContent>
              {dlg?.mode === "attach"
                ? vms.filter((v) => v.resource_group === dlg.disk.resource_group).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)
                : SKUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="bg-[#0078d4] text-white" disabled={!pick} onClick={doDlg} data-testid="az-disk-dlg-submit">{dlg?.mode === "attach" ? "Attach" : "Change tier"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
